import React, { useMemo, useState } from "react";
import {
  Box, Typography, Chip, Button, Stack, TextField, InputAdornment,
  alpha, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Skeleton,
} from "@mui/material";
import { StepUpSpinner } from "../../../Components/Loader";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/Schedule";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import {
  useListParentRequestsQuery,
  useHandleClassRequestMutation,
  useProposeClassRequestAlternativeMutation,
  useScheduleClassRequestMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

const fmt12 = (t: string | undefined | null) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// "2h ago", "3d ago", or falls back to "MMM D · h:mm A" for older items.
const timeAgo = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const then = dayjs(iso);
  const diffMin = dayjs().diff(then, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return then.format("MMM D · h:mm A");
};

// Status → chip styling. Uses only the status values the backend
// actually emits (audited from the previous implementation of this
// page — pending / alternative_proposed / approved / scheduled /
// rejected / cancelled). New statuses would need a backend contract
// change; not inventing any here.
// Status → chip styling. Uses only the status values the backend
// actually emits. Palette intentionally leans Step Up blue / teal /
// green — no amber. `pending` (a.k.a. "needs action") reads as an
// actionable-blue chip rather than a warning-yellow one; this is an
// operational inbox, not an alert dashboard.
const STATUS_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  pending:              { label: "Pending Review",       bg: alpha("#3b82f6", 0.12), fg: "#1d4ed8", border: alpha("#3b82f6", 0.28) },
  alternative_proposed: { label: "Alternative Proposed", bg: alpha("#0ea5e9", 0.12), fg: "#0369a1", border: alpha("#0ea5e9", 0.28) },
  approved:             { label: "Approved",             bg: alpha("#22c55e", 0.14), fg: "#15803d", border: alpha("#22c55e", 0.3) },
  scheduled:            { label: "Scheduled",            bg: alpha("#14b8a6", 0.12), fg: "#0f766e", border: alpha("#14b8a6", 0.28) },
  rejected:             { label: "Rejected",             bg: alpha("#ef4444", 0.14), fg: "#b91c1c", border: alpha("#ef4444", 0.3) },
  cancelled:            { label: "Cancelled",            bg: "rgba(0,0,0,0.06)",     fg: "#6b7280", border: "rgba(0,0,0,0.12)" },
};

// Type badge — Absence (rose) / Makeup (indigo). Icon-first so it
// registers before the eye reads the label.
const TYPE_META: Record<string, { label: string; bg: string; fg: string; border: string; Icon: typeof EventBusyOutlinedIcon }> = {
  absent: {
    label: "Absence", bg: alpha("#ef4444", 0.10), fg: "#b91c1c",
    border: alpha("#ef4444", 0.28), Icon: EventBusyOutlinedIcon,
  },
  makeup: {
    // Makeup shifts from indigo → teal so amber isn't the only warm
    // accent replaced; keeps the palette cool + Step Up.
    label: "Makeup",  bg: alpha("#14b8a6", 0.10), fg: "#0f766e",
    border: alpha("#14b8a6", 0.28), Icon: EventRepeatOutlinedIcon,
  },
};

/**
 * Parent Class Requests — operational queue.
 *
 * Redesign notes:
 *   - The three staff actions (`handleClassRequest`,
 *     `proposeClassRequestAlternative`, `scheduleClassRequest`) and
 *     the source-of-truth query (`useListParentRequestsQuery`) are
 *     preserved verbatim — backend contract untouched.
 *   - Layout switched from a rigid 8-column grid to responsive
 *     premium cards so the same information reads well on tablet /
 *     mobile without a second component.
 *   - Empty state uses the shared `EmptyState` primitive; height is
 *     content-driven, not fixed, so an unfiltered zero-row response
 *     no longer leaves a full-viewport blank rectangle.
 *   - Staff scope: backend routes are permission-gated by
 *     `ClassSchedule_Edit`; this page is the school-wide admin
 *     surface. The teaching-scoped staff variant lives at
 *     /staff-portal/parent-requests and stays separate.
 */
const ParentRequestsQueue: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showMessage, showErrorMessage } = useNotifier();

  const [tab, setTab] = useState<"needs_action" | "absence" | "makeup" | "all">("needs_action");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [pDate, setPDate] = useState("");
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");
  const [pStaff, setPStaff] = useState("");
  const [pNote, setPNote] = useState("");

  // Query args preserved from the original implementation. The
  // needs_action tab drives a server-side status filter; the
  // absence / makeup tabs drive a server-side request_type filter.
  const { data, isLoading, isFetching, refetch } = useListParentRequestsQuery({
    page, per_page: perPage,
    status: tab === "needs_action" ? "needs_action" : "",
    request_type: tab === "absence" ? "absent" : tab === "makeup" ? "makeup" : "",
    search,
  });
  const [handleReq, { isLoading: handling }] = useHandleClassRequestMutation();
  const [propose, { isLoading: proposing }] = useProposeClassRequestAlternativeMutation();
  const [schedule, { isLoading: scheduling }] = useScheduleClassRequestMutation();

  const rows: any[] = (data?.data as any[]) || [];
  const pagination: any = (data as any)?.pagination || {};
  const counts: any = (data as any)?.counts || { needs_action: 0, absence: 0, makeup: 0 };
  const totalCount: number = Number(pagination?.total ?? 0) || 0;
  const selected = useMemo(() => rows.find((r) => r.id === reviewId) || null, [rows, reviewId]);
  const hasActiveFilters = Boolean(search) || tab !== "needs_action";

  // Small helper — same as the original page, wraps mutations with
  // notifier + refetch to avoid stale state after an approve/reject.
  const act = async (fn: () => Promise<any>, okMsg?: string) => {
    try {
      const r = await fn();
      if (r.status) { showMessage(okMsg || r.message); refetch(); return true; }
      showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Action failed"); }
    return false;
  };

  const renderPreference = (r: any) => {
    if (r.request_type === "absent") return "—";
    if (!r.preferred) return "—";
    if (r.scheduling_mode === "specific") {
      return `${dayjs(r.preferred.date).format("MMM D")} · ${fmt12(r.preferred.start_time)}–${fmt12(r.preferred.end_time)}${r.preferred.staff ? ` · ${r.preferred.staff.name}` : ""}`;
    }
    const days = Array.isArray(r.preferred.days) && r.preferred.days.length > 0 ? r.preferred.days.join("/") : "Any day";
    const tod = r.preferred.time_of_day && r.preferred.time_of_day !== "any" ? r.preferred.time_of_day : "Any time";
    return `Flexible · ${days} · ${tod}`;
  };

  const renderClassLabel = (r: any) =>
    [r.class?.grade, r.class?.subject].filter(Boolean).join(" — ") || "Class";

  // ── Filter chips config ─────────────────────────────────────
  const filterChips: Array<{
    key: typeof tab; label: string; count: number; color: string;
  }> = [
    // Active-chip colour scheme: Needs Action = Step Up blue (was
    // amber); Absence = rose; Makeup = teal (was indigo);
    // All = theme primary. Only the ACTIVE chip is saturated —
    // inactive chips stay neutral so the toolbar reads calmly.
    { key: "needs_action", label: "Needs Action", count: Number(counts.needs_action ?? 0), color: "#3b82f6" },
    { key: "absence",      label: "Absence",      count: Number(counts.absence ?? 0),      color: "#ef4444" },
    { key: "makeup",       label: "Makeup",       count: Number(counts.makeup ?? 0),       color: "#14b8a6" },
    { key: "all",          label: "All Requests", count: totalCount,                       color: theme.palette.primary.main },
  ];

  // ── KPI tiles config ────────────────────────────────────────
  const kpiTiles: Array<{
    key: string; label: string; helper: string; value: number;
    color: string; bg: string; Icon: React.ElementType;
  }> = [
    // Palette: blue / rose / teal / green — no amber. Icon block
    // carries the tint; the tile background stays white so a row
    // of four doesn't feel like a warning dashboard.
    {
      key: "needs", label: "Needs Action", helper: "Awaiting review",
      value: Number(counts.needs_action ?? 0),
      color: "#1d4ed8", bg: alpha("#3b82f6", 0.10),
      Icon: PendingActionsOutlinedIcon,
    },
    {
      key: "absence", label: "Absence Requests", helper: "Missed class notices",
      value: Number(counts.absence ?? 0),
      color: "#b91c1c", bg: alpha("#ef4444", 0.08),
      Icon: EventBusyOutlinedIcon,
    },
    {
      key: "makeup", label: "Makeup Requests", helper: "Reschedule asks",
      value: Number(counts.makeup ?? 0),
      color: "#0f766e", bg: alpha("#14b8a6", 0.10),
      Icon: EventRepeatOutlinedIcon,
    },
    {
      key: "total", label: "Total Requests", helper: "Across all statuses",
      value: totalCount,
      color: "#15803d", bg: alpha("#22c55e", 0.10),
      Icon: InboxOutlinedIcon,
    },
  ];

  const clearFilters = () => { setSearch(""); setTab("needs_action"); setPage(1); };

  return (
    <Box sx={{ maxWidth: 1680, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* ── HERO ───────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderRadius: "16px", px: { xs: 2.5, md: 3 }, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 2, mb: 2.5, flexWrap: "wrap",
          border: "1px solid rgba(15,23,42,0.06)",
          background: "linear-gradient(135deg, #ffffff 0%, #fafcff 60%, #f3f7ff 100%)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "1.4px", color: "#64748b", textTransform: "uppercase" }}>
            Parent Requests
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.2 }}>
            Parent Class Requests
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            Review absence and makeup requests submitted by parents.
          </Typography>
        </Box>

        <Box
          sx={{
            // Hero badge — blue/indigo (Step Up primary family), no
            // amber. Reads as an actionable count, not a warning.
            display: "flex", alignItems: "center", gap: 1.25,
            bgcolor: alpha("#3b82f6", 0.10), color: "#1d4ed8",
            border: `1px solid ${alpha("#3b82f6", 0.28)}`,
            borderRadius: "12px", px: 1.75, py: 1, minWidth: 148,
          }}
        >
          <PendingActionsOutlinedIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", lineHeight: 1 }}>
              Needs Action
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>
              {counts.needs_action ?? 0}
              <Box component="span" sx={{ fontSize: 11, fontWeight: 600, ml: 0.75, color: alpha("#1d4ed8", 0.75) }}>
                {counts.needs_action === 1 ? "request" : "requests"}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── KPI ROW ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid", gap: 1.5, mb: 2.5,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
        }}
      >
        {kpiTiles.map((k) => (
          <Box
            key={k.key}
            sx={{
              bgcolor: "#fff", borderRadius: "14px", p: { xs: 1.5, md: 2 },
              border: "1px solid rgba(15,23,42,0.06)",
              display: "flex", alignItems: "center", gap: 1.5, minHeight: 82,
            }}
          >
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "10px",
                bgcolor: k.bg, color: k.color,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <k.Icon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>
                {k.value}
              </Typography>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#0f172a", textTransform: "uppercase", mt: 0.5 }}>
                {k.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {k.helper}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── FILTER TOOLBAR ─────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderRadius: "14px",
          border: "1px solid rgba(15,23,42,0.06)",
          p: { xs: 1.25, md: 1.5 }, mb: 2,
          display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, flex: 1, minWidth: 0 }}>
          {filterChips.map((c) => {
            const active = tab === c.key;
            return (
              <Chip
                key={c.key}
                label={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span">{c.label}</Box>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: 20, height: 18, px: 0.75, borderRadius: 999,
                        fontSize: 10.5, fontWeight: 800,
                        bgcolor: active ? "rgba(255,255,255,0.22)" : alpha(c.color, 0.14),
                        color: active ? "#fff" : c.color,
                      }}
                    >
                      {c.count}
                    </Box>
                  </Box>
                }
                onClick={() => { setTab(c.key); setPage(1); }}
                sx={{
                  height: 34, fontSize: 12.5, fontWeight: 700, borderRadius: "10px", cursor: "pointer",
                  bgcolor: active ? c.color : "#f8fafc",
                  color: active ? "#fff" : "#334155",
                  border: active ? "none" : "1px solid rgba(15,23,42,0.08)",
                  "&:hover": { bgcolor: active ? alpha(c.color, 0.9) : "#eef2f7" },
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            );
          })}
        </Stack>

        <TextField
          size="small"
          placeholder="Search by student, parent, reason…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{
            flexBasis: { xs: "100%", sm: 260, md: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px", height: 38, bgcolor: "#f8fafc",
              "& fieldset": { borderColor: "rgba(15,23,42,0.08)" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearch(""); setPage(1); }} aria-label="Clear search">
                  <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
              sx={{
                border: "1px solid rgba(15,23,42,0.08)", borderRadius: "10px",
                width: 38, height: 38, bgcolor: "#f8fafc",
                "&:hover": { bgcolor: "#eef2f7" },
              }}
              aria-label="Refresh requests"
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: isFetching && !isLoading ? "prq-spin 1s linear infinite" : undefined,
                  "@keyframes prq-spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── LIST ───────────────────────────────────────────── */}
      {isLoading ? (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton
              key={i} variant="rounded" height={128}
              sx={{ borderRadius: "14px", bgcolor: "rgba(15,23,42,0.05)" }}
            />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            bgcolor: "#fff", borderRadius: "14px",
            border: "1px solid rgba(15,23,42,0.06)",
            minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {(() => {
            // Inlined rather than using the shared `EmptyState`
            // primitive: that component forces `opacity: 0.4` and a
            // 48-px SVG size on the icon container, which bleaches
            // the intentional blue/green success wash we want here.
            // Same visual API (icon + title + description + action),
            // just without the muted grey treatment.
            const emptyIconBg = hasActiveFilters ? alpha("#3b82f6", 0.10) : alpha("#22c55e", 0.12);
            const emptyIconFg = hasActiveFilters ? "#1d4ed8" : "#15803d";
            const EmptyIcon = hasActiveFilters ? SearchIcon : CheckCircleOutlineOutlinedIcon;
            return (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 3, py: 5 }}>
                <Box
                  sx={{
                    width: 64, height: 64, borderRadius: "50%",
                    bgcolor: emptyIconBg, color: emptyIconFg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <EmptyIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                  {hasActiveFilters ? "No requests match your filters" : "You're all caught up"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 340 }}>
                  {hasActiveFilters
                    ? "Try another request type or clear your search."
                    : "No parent requests need attention right now."}
                </Typography>
                {hasActiveFilters && (
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      mt: 2.5, textTransform: "none", fontSize: 12.5, fontWeight: 700,
                      color: "#1d4ed8",
                      "&:hover": { bgcolor: alpha("#3b82f6", 0.08) },
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </Box>
            );
          })()}
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {rows.map((r) => {
            const status = STATUS_META[r.status] || STATUS_META.pending;
            const type = TYPE_META[r.request_type as "absent" | "makeup"] || TYPE_META.absent;
            const TypeIcon = type.Icon;
            const submittedLabel = timeAgo(r.submitted_at);
            return (
              <Box
                key={r.id}
                onClick={() => setReviewId(r.id)}
                sx={{
                  bgcolor: "#fff", borderRadius: "14px",
                  border: "1px solid rgba(15,23,42,0.06)",
                  borderLeft: `3px solid ${type.fg}`,
                  p: { xs: 1.75, md: 2 },
                  cursor: "pointer",
                  transition: "box-shadow 120ms ease, transform 120ms ease",
                  "&:hover": {
                    boxShadow: "0 6px 20px rgba(15,23,42,0.08)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {/* Top row: type badge + status + submitted */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Box
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      bgcolor: type.bg, color: type.fg,
                      border: `1px solid ${type.border}`,
                      borderRadius: 999, px: 1, py: 0.25,
                      fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                    }}
                  >
                    <TypeIcon sx={{ fontSize: 14 }} />
                    {type.label}
                  </Box>
                  <Box
                    sx={{
                      display: "inline-flex", alignItems: "center",
                      bgcolor: status.bg, color: status.fg,
                      border: `1px solid ${status.border}`,
                      borderRadius: 999, px: 1, py: 0.25,
                      fontSize: 10.5, fontWeight: 800, letterSpacing: "0.4px",
                    }}
                  >
                    {status.label}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {submittedLabel && (
                    <Typography sx={{ fontSize: 11, color: "text.disabled", fontWeight: 600 }}>
                      Submitted {submittedLabel}
                    </Typography>
                  )}
                </Box>

                {/* Middle: identity + facts */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" },
                    gap: { xs: 0.75, md: 2 },
                    alignItems: "start",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                      {r.student?.name || "Student"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: "text.secondary" }}>
                      <PersonOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: 12 }}>
                        {r.parent?.name || r.student?.parent?.name || "Parent"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                      <SchoolOutlinedIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
                        {renderClassLabel(r)}
                      </Typography>
                    </Box>
                    {r.class?.date && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: "text.secondary" }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: 11.5 }}>
                          {dayjs(r.class.date).format("MMM D, YYYY")}
                          {r.class.start_time && (
                            <>
                              {" · "}{fmt12(r.class.start_time)}
                              {r.class.end_time ? `–${fmt12(r.class.end_time)}` : ""}
                            </>
                          )}
                        </Typography>
                      </Box>
                    )}
                    {r.assigned_staff?.name && (
                      <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}>
                        Teacher: {r.assigned_staff.name}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    {r.request_type === "makeup" && (
                      <>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#64748b", textTransform: "uppercase", mb: 0.25 }}>
                          Preference
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, color: "text.secondary" }}>
                          <ScheduleOutlinedIcon sx={{ fontSize: 14, mt: 0.25 }} />
                          <Typography sx={{ fontSize: 12 }}>
                            {renderPreference(r)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {r.reason && (
                      <>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#64748b", textTransform: "uppercase", mb: 0.25, mt: r.request_type === "makeup" ? 1 : 0 }}>
                          Reason
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12, color: "#334155", fontStyle: "italic",
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          “{r.reason}”
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Footer action */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.25 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); setReviewId(r.id); }}
                    sx={{
                      textTransform: "none", fontSize: 12, fontWeight: 700,
                      borderColor: "rgba(15,23,42,0.16)", color: "#0f172a",
                      "&:hover": { borderColor: "rgba(15,23,42,0.35)", bgcolor: "#f8fafc" },
                    }}
                  >
                    Review
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2.5 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} sx={{ textTransform: "none" }}>Previous</Button>
          <Typography sx={{ fontSize: 12, alignSelf: "center", color: "text.secondary" }}>
            Page {page} of {pagination.last_page}
          </Typography>
          <Button size="small" disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)} sx={{ textTransform: "none" }}>Next</Button>
        </Box>
      )}

      {/* ── REVIEW DIALOG (unchanged actions/behaviour) ───── */}
      <Dialog
        open={!!reviewId}
        onClose={() => { setReviewId(null); setProposeOpen(false); }}
        maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
      >
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 800, pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              {(() => {
                const meta = TYPE_META[selected.request_type as "absent" | "makeup"] || TYPE_META.absent;
                const DlgIcon = meta.Icon;
                return <DlgIcon sx={{ fontSize: 22, color: meta.fg }} />;
              })()}
              <Box component="span">
                {selected.request_type === "absent" ? "Absence Request" : "Makeup Request"}
              </Box>
              <Chip
                label={(STATUS_META[selected.status] || STATUS_META.pending).label}
                size="small"
                sx={{
                  ml: "auto", height: 22, fontSize: 10.5, fontWeight: 800,
                  bgcolor: (STATUS_META[selected.status] || STATUS_META.pending).bg,
                  color: (STATUS_META[selected.status] || STATUS_META.pending).fg,
                }}
              />
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.25} sx={{ fontSize: 13 }}>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{selected.student?.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{renderClassLabel(selected)}</Typography>
                  {selected.class?.date && (
                    <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>
                      {dayjs(selected.class.date).format("dddd, MMMM D, YYYY")}
                      {selected.class.start_time && (
                        <> · {fmt12(selected.class.start_time)} – {fmt12(selected.class.end_time)}</>
                      )}
                    </Typography>
                  )}
                </Box>
                {selected.assigned_staff && (
                  <Typography sx={{ fontSize: 12 }}><b>Original Teacher:</b> {selected.assigned_staff.name}</Typography>
                )}
                <Typography sx={{ fontSize: 12 }}><b>Reason:</b> {selected.reason || "—"}</Typography>
                <Typography sx={{ fontSize: 12 }}><b>Submitted:</b> {selected.submitted_at ? dayjs(selected.submitted_at).format("MMM D, YYYY · h:mm A") : "—"}</Typography>

                {selected.request_type === "makeup" && selected.preferred && (
                  <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#6366f1", 0.06) }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#4338ca", textTransform: "uppercase", mb: 0.25, letterSpacing: "0.4px" }}>Parent Preference</Typography>
                    <Typography sx={{ fontSize: 12 }}>{renderPreference(selected)}</Typography>
                  </Box>
                )}

                {selected.proposal && (
                  <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#8b5cf6", 0.06) }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#6d28d9", textTransform: "uppercase", mb: 0.25, letterSpacing: "0.4px" }}>
                      {selected.status === "alternative_proposed" ? "Proposed to Parent" : "Previous Proposal"}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {dayjs(selected.proposal.date).format("ddd, MMM D")} · {fmt12(selected.proposal.start_time)} – {fmt12(selected.proposal.end_time)}
                      {selected.proposal.staff ? ` · ${selected.proposal.staff.name}` : ""}
                    </Typography>
                    {selected.proposal.note && <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontStyle: "italic" }}>{selected.proposal.note}</Typography>}
                  </Box>
                )}

                {selected.final_makeup_occurrence_id && (
                  <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#22c55e", 0.06) }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#15803d" }}>
                      Makeup scheduled (occurrence #{selected.final_makeup_occurrence_id})
                    </Typography>
                  </Box>
                )}

                {/* Inline propose form (unchanged) */}
                {selected.request_type === "makeup" && proposeOpen && (
                  <Box sx={{ p: 1.25, borderRadius: "10px", border: `1px solid ${alpha("#8b5cf6", 0.2)}` }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>Propose Alternative Time</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 1 }}>
                      <TextField size="small" label="Date" type="date" InputLabelProps={{ shrink: true }} value={pDate} onChange={(e) => setPDate(e.target.value)} />
                      <TextField size="small" label="Start" type="time" InputLabelProps={{ shrink: true }} value={pStart} onChange={(e) => setPStart(e.target.value)} />
                      <TextField size="small" label="End" type="time" InputLabelProps={{ shrink: true }} value={pEnd} onChange={(e) => setPEnd(e.target.value)} />
                      <TextField size="small" label="Teacher ID" type="number" value={pStaff} onChange={(e) => setPStaff(e.target.value)} />
                      <TextField size="small" label="Note (optional)" value={pNote} onChange={(e) => setPNote(e.target.value)} sx={{ gridColumn: "1 / span 3" }} />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small" variant="contained"
                        disabled={proposing || !pDate || !pStart || !pEnd || !pStaff}
                        startIcon={proposing ? <StepUpSpinner size="small" /> : undefined}
                        onClick={async () => {
                          const ok = await act(() => propose({
                            id: selected.id,
                            proposed_date: pDate, proposed_start_time: pStart, proposed_end_time: pEnd,
                            proposed_staff_id: Number(pStaff),
                            proposal_note: pNote || undefined,
                          }).unwrap(), "Alternative proposed.");
                          if (ok) { setProposeOpen(false); setPDate(""); setPStart(""); setPEnd(""); setPStaff(""); setPNote(""); }
                        }}
                        sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700 }}
                      >
                        Send Proposal
                      </Button>
                      <Button size="small" onClick={() => setProposeOpen(false)} sx={{ textTransform: "none", fontSize: 11.5 }}>Cancel</Button>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 0.75 }}>
              {selected.request_type === "absent" ? (
                <>
                  {selected.status === "pending" && (
                    <>
                      <Button
                        variant="contained" disabled={handling}
                        startIcon={handling ? <StepUpSpinner size="small" /> : undefined}
                        onClick={() => act(() => handleReq({ id: selected.id, status: "approved" }).unwrap(), "Absence approved.")}
                        sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined" disabled={handling}
                        onClick={() => act(() => handleReq({ id: selected.id, status: "rejected" }).unwrap(), "Absence rejected.")}
                        sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#dc2626", borderColor: alpha("#ef4444", 0.4) }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  {(selected.status === "pending" || selected.status === "approved") && (
                    <Button
                      variant="contained" disabled={scheduling}
                      startIcon={scheduling ? <StepUpSpinner size="small" /> : undefined}
                      onClick={() => act(() => schedule({ id: selected.id }).unwrap(), "Makeup scheduled.")}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                    >
                      {selected.status === "approved" ? "Schedule Makeup" : "Approve & Schedule"}
                    </Button>
                  )}
                  {selected.status !== "scheduled" && selected.status !== "rejected" && selected.status !== "cancelled" && (
                    <Button variant="outlined" disabled={proposing}
                      onClick={() => setProposeOpen((v) => !v)}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}>
                      {proposeOpen ? "Hide Proposal Form" : "Suggest Another Time"}
                    </Button>
                  )}
                  {(selected.status === "pending" || selected.status === "alternative_proposed") && (
                    <Button variant="outlined" disabled={handling}
                      onClick={() => act(() => handleReq({ id: selected.id, status: "rejected" }).unwrap(), "Request declined.")}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#dc2626", borderColor: alpha("#ef4444", 0.4) }}>
                      Decline
                    </Button>
                  )}
                  {selected.status === "alternative_proposed" && (
                    <Typography sx={{ fontSize: 11, color: "text.secondary", ml: "auto", alignSelf: "center" }}>Awaiting parent response…</Typography>
                  )}
                </>
              )}
              <Button onClick={() => { setReviewId(null); setProposeOpen(false); }} sx={{ textTransform: "none", ml: "auto" }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ParentRequestsQueue;
